import { Injectable } from '@angular/core'
import {
  HttpService,
  IExportFileResponse,
  IAvailableWeeksResponse,
  INew_TableAction,
  ITimePeriodRequest,
  TableActionsService
} from '@lla-platform/core/core-data-access'
import { DateService } from '@lla-platform/core/core-util'
import { Observable } from 'rxjs'
import { TARGETS_API_URL } from './targets-api-routes'
import {
  IPeriodTargetDetailsResponse,
  IPeriodTargetResponse,
  IWeekTargetResponse
} from '../interfaces/weekly-target.interface'
import { ITargetsHistoryRequest, ITargetsHistoryResponse } from '../interfaces/targets-history.interface'
import {
  ITargetsDailyAverageRequest,
  ITargetsDailyAverageResponse
} from '../interfaces/targets-daily-average.interface'
import { IYearlyTargetsResponse } from '../interfaces/yearly-targets.interface'
import {
  IMonthlyTargetsResponse,
  ISaveLocationMonthlyTargetsRequest
} from '../interfaces/monthly-targets.interface'
import {
  IAdminExportTargetsRequest,
  IAdminMonthlyViewTargetsRequest,
  IAdminMonthlyViewTargetsResponse,
  IAdminWeeklyTargetsRequest,
  IAdminWeeklyTargetsResponse
} from '../interfaces/admin-view-targets.interface'
import {
  ILocationDailyTargetsResponse,
  ISaveLocationDailyTargetsRequest
} from '../interfaces/location-daily-targets.interface'
import {
  IAutoGenerateDailyTargetsPreviewResponse,
  IAutoGenerateDailyTargetsRequest
} from '../interfaces/auto-generate-targets.interface'

@Injectable({
  providedIn: 'root'
})
export class TargetsService {
  constructor(
    private http: HttpService,
    private tableActionsService: TableActionsService,
    private dateService: DateService
  ) {}

  getAvailableWeeks(): Observable<IAvailableWeeksResponse> {
    return this.http.get(TARGETS_API_URL.availableWeeks)
  }

  getShopsTargets(weekNumber: number): Observable<IWeekTargetResponse> {
    return this.http.post(TARGETS_API_URL.shopsTargets, { weekNumber })
  }

  getShopsTargetsComps(weekNumber: number): Observable<IWeekTargetResponse> {
    return this.http.post(TARGETS_API_URL.shopsTargetsComps, { weekNumber })
  }

  getShopsTargetsByPeriod(payload: ITimePeriodRequest): Observable<IPeriodTargetResponse> {
    return this.http.post(
      TARGETS_API_URL.shopsTargetsByPeriod,
      this.dateService.generateTimePeriod(payload)
    )
  }

  getShopsTargetsByPeriodDetails(
    payload: ITimePeriodRequest,
    locationIds: string[]
  ): Observable<IPeriodTargetDetailsResponse> {
    return this.http.post(TARGETS_API_URL.shopsTargetsByPeriodDetails, {
      ...this.dateService.generateTimePeriod(payload),
      locationIds
    })
  }

  getTargetsHistory(payload: ITargetsHistoryRequest): Observable<ITargetsHistoryResponse> {
    return this.http.post(TARGETS_API_URL.targetsHistory, payload)
  }

  getTargetsDailyAverage(
    payload: ITargetsDailyAverageRequest
  ): Observable<ITargetsDailyAverageResponse> {
    return this.http.post(TARGETS_API_URL.targetsDailyAverage, payload)
  }

  exportTargetsDailyAverage(payload: ITargetsDailyAverageRequest): Observable<IExportFileResponse> {
    return this.http.post(TARGETS_API_URL.exportTargetsDailyAverage, payload)
  }

  getYearlyTargets(info: INew_TableAction, year: number): Observable<IYearlyTargetsResponse> {
    return this.http.post(TARGETS_API_URL.yearlyTargets, {
      year,
      ...this.tableActionsService.createRequestPayload(info)
    })
  }

  exportYearlyTargets(info: INew_TableAction, year: number): Observable<IExportFileResponse> {
    return this.http.post(TARGETS_API_URL.exportYearlyTargets, {
      year,
      ...this.tableActionsService.createRequestPayload(info)
    })
  }

  // User-facing yearly targets page consumes the same daily payload as admin;
  // aggregation happens client-side in LocationYearlyTargetsComponent.
  getLocationYearlyTargets(locationId: string, year: number): Observable<ILocationDailyTargetsResponse> {
    return this.http.get(TARGETS_API_URL.locationYearlyTargets(locationId, year))
  }

  // Admin APIs
  adminGetYearlyTargets(info: INew_TableAction, year: number): Observable<IYearlyTargetsResponse> {
    return this.http.post(TARGETS_API_URL.adminYearlyTargets, {
      year,
      ...this.tableActionsService.createRequestPayload(info)
    })
  }

  adminExportYearlyTargets(info: INew_TableAction, year: number): Observable<IExportFileResponse> {
    return this.http.post(TARGETS_API_URL.adminExportYearlyTargets, {
      year,
      ...this.tableActionsService.createRequestPayload(info)
    })
  }

  adminGetLocationDailyTargets(
    locationId: string,
    year: number
  ): Observable<ILocationDailyTargetsResponse> {
    return this.http.get(TARGETS_API_URL.adminLocationDailyTargets(locationId, year))
  }

  adminSaveLocationDailyTargets(
    locationId: string,
    payload: ISaveLocationDailyTargetsRequest
  ): Observable<void> {
    return this.http.post(TARGETS_API_URL.adminSaveLocationDailyTargets(locationId), payload)
  }

  adminGetMonthlyTargets(info: INew_TableAction, year: number): Observable<IMonthlyTargetsResponse> {
    return this.http.post(TARGETS_API_URL.adminMonthlyTargets, {
      year,
      ...this.tableActionsService.createRequestPayload(info)
    })
  }

  adminExportMonthlyTargets(info: INew_TableAction, year: number): Observable<IExportFileResponse> {
    return this.http.post(TARGETS_API_URL.adminExportMonthlyTargets, {
      year,
      ...this.tableActionsService.createRequestPayload(info)
    })
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  adminGetLocationMonthlyTargets(locationId: string, year: number): Observable<any> {
    return this.http.get(TARGETS_API_URL.adminLocationMonthlyTargets(locationId, year))
  }

  adminSaveLocationMonthlyTargets(
    locationId: string,
    payload: ISaveLocationMonthlyTargetsRequest
  ): Observable<void> {
    return this.http.post(TARGETS_API_URL.adminSaveLocationMonthlyTargets(locationId), payload)
  }

  adminPreviewAutoGenerateDailyTargets(
    payload: IAutoGenerateDailyTargetsRequest
  ): Observable<IAutoGenerateDailyTargetsPreviewResponse> {
    return this.http.post(TARGETS_API_URL.adminAutoGenerateDailyTargetsPreview, payload)
  }

  adminApplyAutoGenerateDailyTargets(payload: IAutoGenerateDailyTargetsRequest): Observable<void> {
    return this.http.post(TARGETS_API_URL.adminAutoGenerateDailyTargetsApply, payload)
  }

  adminGetWeeklyTargets(payload: IAdminWeeklyTargetsRequest): Observable<IAdminWeeklyTargetsResponse> {
    return this.http.post(TARGETS_API_URL.adminWeeklyTargets, payload)
  }

  adminGetMonthlyViewTargets(
    payload: IAdminMonthlyViewTargetsRequest
  ): Observable<IAdminMonthlyViewTargetsResponse> {
    return this.http.post(TARGETS_API_URL.adminMonthlyViewTargets, payload)
  }

  adminExportWeeklyTargets(payload: IAdminExportTargetsRequest): Observable<IExportFileResponse> {
    return this.http.post(TARGETS_API_URL.adminExportWeeklyTargets, payload)
  }

  adminExportMonthlyViewTargets(payload: IAdminExportTargetsRequest): Observable<IExportFileResponse> {
    return this.http.post(TARGETS_API_URL.adminExportMonthlyViewTargets, payload)
  }
}
